/**
 * Source-aware US clinic outcome registry contracts.
 *
 * This is deliberately separate from the flattened Clinic model. A clinic
 * can have many source rows for the same reporting year, and source systems
 * do not necessarily use compatible endpoints or denominators.
 */

export type USClinicSourceSystem = "cdc_nass" | "sart_cors" | "asrm";

export type SourceCoverageStatus =
  | "available"
  | "recovered"
  | "requires_recovery"
  | "searched_not_found"
  | "unreleased"
  | "inaccessible"
  | "restricted"
  | "non_comparable";

export type SourceAccessStatus =
  "public" | "restricted" | "unknown" | "not_applicable";

export type SourceRecordClass =
  | "clinic_outcome"
  | "clinic_profile"
  | "national_benchmark"
  | "contextual_evidence";

export type RegistryEndpoint =
  | "live_birth_per_transfer"
  | "live_birth_per_retrieval"
  | "live_birth_per_treatment_attempt"
  | "clinical_pregnancy"
  | "pregnancy"
  | "delivery"
  | "implantation"
  | "fertilization"
  | "blastulation"
  | "survival"
  | "unknown";

export type RegistryTreatmentType =
  | "ivf"
  | "icsi"
  | "iui"
  | "ovulation_induction"
  | "frozen_embryo_transfer"
  | "other"
  | "unknown";

export type RegistryPopulation =
  "own_eggs" | "donor_eggs" | "donated_embryos" | "all_patients" | "unknown";

export type RegistryEmbryoSource =
  | "fresh_from_fresh_eggs"
  | "fresh_from_frozen_eggs"
  | "frozen_embryos"
  | "donated_embryos"
  | "not_applicable"
  | "unknown";

export type RegistryAvailability =
  | "published"
  | "published_missing_denominator"
  | "suppressed"
  | "unavailable"
  | "non_comparable";

export type RegistryValidationStatus =
  "pending" | "validated" | "rejected" | "not_applicable";

export type RegistryClinicalReviewStatus =
  "pending" | "reviewed" | "not_reviewed" | "not_applicable";

export interface SourceArtifactReference {
  id: string;
  sourceUrl: string;
  fetchedAt?: string;
  checksum?: string;
  version?: string;
  storageStatus: "preserved" | "referenced" | "not_obtained";
}

export interface SourceCoverageEntry {
  id: string;
  sourceSystem: USClinicSourceSystem;
  recordClass: SourceRecordClass;
  scope: "US" | "global" | "unknown";
  reportingYear?: number;
  yearStart?: number;
  yearEnd?: number;
  datasetId?: string;
  datasetName?: string;
  sourceUrl: string;
  accessStatus: SourceAccessStatus;
  status: SourceCoverageStatus;
  availabilityBasis?:
    | "source_documented"
    | "catalog_entry_recovered"
    | "artifact_recovered"
    | "searched_not_found"
    | "access_assessed";
  dimensions: string[];
  notes: string;
}

export interface RegistryPrecision {
  low?: number;
  high?: number;
  confidenceLevel?: number;
}

export interface RegistrySourceRowReference {
  datasetId?: string;
  datasetName?: string;
  questionId?: string;
  topicId?: string;
  breakoutId?: string;
  rowId?: string;
  reference?: string;
}

export interface NormalizedClinicOutcomeCell {
  sourceSystem: Exclude<USClinicSourceSystem, "asrm">;
  sourceType: "national_mandatory_registry" | "registry_participation";
  country: "US";
  clinicId?: string;
  sourceClinicId: string;
  reportingYear: number;
  sourcePublicationDate?: string;
  sourceRow: RegistrySourceRowReference;
  treatmentType: RegistryTreatmentType;
  population: RegistryPopulation;
  embryoSource: RegistryEmbryoSource;
  ageBand?: string;
  endpoint: RegistryEndpoint;
  availability: RegistryAvailability;
  /** Published percentage, kept separate from precision and counts. */
  publishedRate?: number;
  precision?: RegistryPrecision;
  numerator?: number;
  denominator?: number;
  confidenceInterval?: RegistryPrecision;
  denominatorDescription?: string;
  reportingPopulation?: string;
  rawArtifact: SourceArtifactReference;
  sourceUrl: string;
  fetchedAt?: string;
  provenance: {
    validationStatus: RegistryValidationStatus;
    clinicalReviewStatus: RegistryClinicalReviewStatus;
    notes?: string;
  };
}

export interface RegistryValidationResult {
  valid: boolean;
  errors: string[];
}

/**
 * Validate invariants that protect the projection adapter from unsafe source
 * rows. In particular, annual clinic volume is intentionally not a field on
 * an outcome cell and cannot be used as its denominator.
 */
export function validateNormalizedClinicOutcomeCell(
  cell: NormalizedClinicOutcomeCell,
): RegistryValidationResult {
  const errors: string[] = [];

  if (cell.country !== "US") {
    errors.push("clinic outcome cells must be explicitly scoped to the US");
  }
  if (!cell.sourceClinicId.trim()) {
    errors.push("sourceClinicId is required");
  }
  if (!Number.isInteger(cell.reportingYear) || cell.reportingYear < 0) {
    errors.push("reportingYear must be a non-negative integer");
  }
  if (cell.publishedRate !== undefined) {
    if (
      !Number.isFinite(cell.publishedRate) ||
      cell.publishedRate < 0 ||
      cell.publishedRate > 100
    ) {
      errors.push("publishedRate must be a percentage between 0 and 100");
    }
  }
  if (
    cell.numerator !== undefined &&
    (!Number.isInteger(cell.numerator) || cell.numerator < 0)
  ) {
    errors.push("numerator must be a non-negative integer");
  }
  if (
    cell.denominator !== undefined &&
    (!Number.isInteger(cell.denominator) || cell.denominator <= 0)
  ) {
    errors.push("denominator must be a positive integer");
  }
  if (
    cell.numerator !== undefined &&
    cell.denominator !== undefined &&
    cell.numerator > cell.denominator
  ) {
    errors.push("numerator cannot exceed denominator");
  }
  if (cell.availability === "published" && cell.publishedRate === undefined) {
    errors.push("published cells require a publishedRate");
  }
  if (
    cell.availability === "published_missing_denominator" &&
    cell.publishedRate === undefined
  ) {
    errors.push("published_missing_denominator cells require a publishedRate");
  }
  if (cell.availability === "suppressed" && cell.publishedRate !== undefined) {
    errors.push("suppressed cells cannot expose a publishedRate");
  }
  if (!cell.rawArtifact.id || !cell.rawArtifact.sourceUrl) {
    errors.push("rawArtifact id and sourceUrl are required");
  }
  if (!cell.sourceUrl) {
    errors.push("sourceUrl is required");
  }

  return { valid: errors.length === 0, errors };
}

/**
 * Parse the CDC convention for a published rate cell without inventing a
 * denominator. Fractions retain both counts; decimal/percentage strings keep
 * only the published percentage; suppression markers remain suppressed.
 */
export function parsePublishedPercentage(
  value: string | number | null | undefined,
): Pick<
  NormalizedClinicOutcomeCell,
  "availability" | "publishedRate" | "numerator" | "denominator"
> {
  if (value === null || value === undefined || String(value).trim() === "") {
    return { availability: "unavailable" };
  }

  const rawText = String(value).trim();
  // Socrata retains literal quotes around some CDC fractions (e.g. '"5/19"').
  const text =
    rawText.startsWith('"') && rawText.endsWith('"')
      ? rawText.slice(1, -1).trim()
      : rawText;
  if (text === "") {
    return { availability: "unavailable" };
  }
  if (text.includes("*")) {
    return { availability: "suppressed" };
  }

  const fraction = text.match(/^(\d+(?:\.\d+)?)\s*\/\s*(\d+(?:\.\d+)?)$/);
  if (fraction) {
    const numerator = Number(fraction[1]);
    const denominator = Number(fraction[2]);
    if (
      Number.isFinite(numerator) &&
      Number.isFinite(denominator) &&
      denominator > 0 &&
      numerator >= 0 &&
      numerator <= denominator
    ) {
      return {
        availability: "published",
        publishedRate: (numerator / denominator) * 100,
        numerator,
        denominator,
      };
    }
    return { availability: "unavailable" };
  }

  const parsed = Number(text.replace(/%$/, ""));
  return Number.isFinite(parsed) && parsed >= 0 && parsed <= 100
    ? { availability: "published_missing_denominator", publishedRate: parsed }
    : { availability: "unavailable" };
}
